import { ReactNode } from "react";

import { StepStatus } from "./helper";

type Tone = {
  border: string;
  iconBg: string;
  iconColor: string;
};

const TONES: Record<StepStatus, Tone> = {
  complete: {
    border: "color-mix(in srgb, var(--green-500) 30%, var(--brand-white))",
    iconBg: "var(--green-100)",
    iconColor: "var(--green-700)",
  },
  current: {
    border:
      "color-mix(in srgb, var(--brand-purple-500) 45%, var(--brand-white))",
    iconBg: "var(--brand-purple-600)",
    iconColor: "var(--brand-white)",
  },
  upcoming: {
    border: "var(--brand-gray-200)",
    iconBg: "var(--brand-gray-100)",
    iconColor: "var(--brand-gray-600)",
  },
};

interface IStepCardProps {
  title: string;
  description?: string;
  icon: ReactNode;
  status: StepStatus;
  action?: ReactNode;
  hidden?: boolean;
  bodyClassName?: string;
  "data-testid"?: string;
  /** CLE-3015 — a `TourAnchor` value for the card (`data-tour`). */
  "data-tour"?: string;
  children: ReactNode;
}

export function StepCard({
  title,
  description,
  icon,
  status,
  action,
  hidden = false,
  bodyClassName = "px-3 pb-3",
  "data-testid": dataTestId,
  "data-tour": dataTour,
  children,
}: IStepCardProps) {
  const tone = TONES[status];

  return (
    <section
      data-testid={dataTestId}
      data-tour={dataTour}
      className="bg-white border-1 border-round-2xl mb-4"
      style={{
        borderColor: tone.border,
        // PrimeReact's TabView scrolls the clicked header into view, which scrolls an `overflow: hidden` card sideways when its content overflows by a pixel; `clip` cannot be scrolled.
        overflow: "clip",
        ...(hidden ? { display: "none" } : {}),
      }}
    >
      <div className="flex align-items-center gap-3 p-3">
        <div
          className="flex align-items-center justify-content-center border-circle flex-shrink-0"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            background: tone.iconBg,
            color: tone.iconColor,
          }}
        >
          {status === "complete" ? (
            <i className="pi pi-check text-base" />
          ) : (
            icon
          )}
        </div>

        <div className="flex flex-column gap-1">
          <span
            className="text-base font-bold line-height-2"
            style={{ color: "var(--brand-black)" }}
          >
            {title}
          </span>

          {!!description && (
            <span
              className="text-xs line-height-2"
              style={{ color: "var(--brand-gray-600)" }}
            >
              {description}
            </span>
          )}
        </div>

        {!!action && (
          <div className="flex flex-1 justify-content-end align-items-start">
            {action}
          </div>
        )}
      </div>

      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
